import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
  /**
   * Rows currently selected.
   *
   * Omitted by tables that have no selection, which then report their size
   * plainly instead. A read-only list saying "0 of 432 row(s) selected" invites
   * the reader to look for a checkbox that was never there.
   */
  selectedCount?: number;
}

export function Pagination({
  page,
  pageSize,
  total,
  totalPages,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 30, 40, 50],
  selectedCount,
}: PaginationProps) {
  return (
    <div className="flex flex-col gap-4 px-2 py-4 sm:flex-row sm:items-center sm:justify-between">
      {/* Row count — hidden on mobile. Tables without selection still report
          their size, they just do not claim a selection that cannot be made. */}
      <div className="hidden text-sm text-muted-foreground sm:block sm:flex-1">
        {selectedCount === undefined
          ? `${total} row${total === 1 ? '' : 's'}`
          : `${selectedCount} of ${total} row(s) selected.`}
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center justify-center gap-3 sm:justify-end sm:gap-6 lg:gap-8">
        {/* Page size selector - hidden on mobile */}
        {onPageSizeChange && (
          <div className="hidden items-center space-x-2 sm:flex">
            <p className="text-sm text-muted-foreground">Rows per page</p>
            <Select
              value={String(pageSize)}
              onValueChange={(value) => onPageSizeChange(Number(value))}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue placeholder={pageSize} />
              </SelectTrigger>
              <SelectContent side="top">
                {pageSizeOptions.map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Page info */}
        <div className="flex items-center justify-center text-sm text-muted-foreground">
          Page {page} of {totalPages || 1}
        </div>

        {/* Navigation buttons */}
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            className="h-8 px-2 sm:px-3"
            aria-label="Previous page"
          >
            <ChevronLeft className="h-4 w-4 sm:mr-1" />
            <span className="hidden sm:inline">Previous</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
            className="h-8 px-2 sm:px-3"
            aria-label="Next page"
          >
            <span className="hidden sm:inline">Next</span>
            <ChevronRight className="h-4 w-4 sm:ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}
